<template>
  <BaseVeeForm
    :action="storeTagCategory"
    ensure-error-keys-camel-case
    v-slot="{ isPending, invalid }"
    class="space-y-8"
  >
    <VModal ref="modal" @modal:hidden="$emit('hide')" :disable-backdrop-click="isPending">
      <BaseFetchData :action="loadTagCategoryTemplates">
        <template #default="fetchDataProps">
          <div class="mx-auto w-full max-w-lg">
            <BaseCard>
              <BaseCardHeader> Import Tag Category </BaseCardHeader>
              <BaseCardBody v-if="fetchDataProps.status.loading">
                <Loader />
              </BaseCardBody>
              <template v-else-if="fetchDataProps.status.resolved">
                <BaseCardBody class="space-y-4">
                  <ValidationProvider
                    rules="required"
                    name="tagCategoryId"
                    slim
                    vid="tagCategoryId"
                  >
                    <template #default="{ errors }">
                      <BaseFormGroup>
                        <BaseFormLabel>Tag category</BaseFormLabel>
                        <div>
                          <BaseFormSelect
                            required
                            v-model="formData.tagCategoryId"
                            :options="tagCategories"
                          />
                          <BaseFormInlineMessage v-if="errors.length" danger>
                            {{ errors[0] }}
                          </BaseFormInlineMessage>
                        </div>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>

                  <ValidationProvider
                    v-if="formData.tagCategoryId"
                    rules="required"
                    name="tags"
                    slim
                    vid="tagIds"
                  >
                    <template #default="{ errors }">
                      <BaseFormGroup>
                        <BaseFormLabel>
                          Tags
                          <a role="button" href="#" @click.prevent="selectAllTags">select all</a>
                          <template v-if="formData.tagIds.length > 0">
                            | <a role="button" href="#" @click.prevent="clearSelectedTags">clear</a>
                          </template>
                        </BaseFormLabel>
                        <div class="-mx-1">
                          <label
                            v-for="tag in tags"
                            :key="tag.value"
                            :class="[
                              formData.tagIds.indexOf(tag.value) >= 0
                                ? 'border-transparent bg-blue-100 text-blue-800'
                                : 'border-gray-200 text-gray-800',
                            ]"
                            class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium border m-1"
                          >
                            <input
                              v-model="formData.tagIds"
                              type="checkbox"
                              name="tags"
                              :value="tag.value"
                              :disabled="isPending"
                              class="hidden"
                            />
                            {{ tag.label }}
                          </label>
                        </div>
                        <BaseFormInlineMessage v-if="errors.length" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                </BaseCardBody>
                <BaseCardFooter>
                  <div class="flex space-x-2">
                    <BaseButton primary :disabled="invalid || isPending"> Import </BaseButton>
                    <BaseButton
                      type="button"
                      primary
                      outline
                      :disabled="isPending"
                      @click="$refs.modal.close()"
                    >
                      Cancel
                    </BaseButton>
                  </div>
                </BaseCardFooter>
              </template>
            </BaseCard>
          </div>
        </template>
      </BaseFetchData>
    </VModal>
  </BaseVeeForm>
</template>

<script>
import { templatesClient, templateTagCategoryTagsClient } from '@/api/clients';
import mapKeysToCamelCase from '@/utils/map-keys-to-camel-case';
import mapKeysToSnakeCase from '@/utils/map-keys-to-snake-case';

export default {
  props: {
    action: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      formData: {
        tagCategoryId: null,
        tagIds: [],
      },
      tagCategories: [],
      tags: [],
    };
  },
  watch: {
    'formData.tagCategoryId': 'onTagCategoryChange',
  },
  methods: {
    async loadTagCategoryTemplates() {
      try {
        const response = await templatesClient.tagCategories();

        this.tagCategories = response.data.map(mapKeysToCamelCase).map(({ id, name }) => ({
          label: name,
          value: id,
        }));
      } catch (error) {
        // handle error
      }
    },
    async loadTagCategoryTemplateTags() {
      try {
        const response = await templateTagCategoryTagsClient.list(this.formData.tagCategoryId);

        this.tags = response.data.map(mapKeysToCamelCase).map(({ id, name }) => ({
          label: name,
          value: id,
        }));
      } catch (error) {
        // handle error
      }
    },
    clearSelectedTags() {
      this.formData.tagIds = [];
    },
    onTagCategoryChange() {
      this.clearSelectedTags();
      this.loadTagCategoryTemplateTags();
    },
    selectAllTags() {
      this.formData.tagIds = this.tags.map((tag) => tag.value);
    },
    async storeTagCategory() {
      try {
        const response = await this.action(mapKeysToSnakeCase(this.formData));

        this.$emit('tag-category-template-imported', response.data);

        this.$refs.modal.close();

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
